import { useState } from "react";
import { successMessage } from "../../../../helpers/ErrorHandler";
import { resolveDuplicateVersionNameError } from "../../mapping-version/helpers/mappingVersion.helpers";
import { useUpdateVersionMetadata } from "../../../../api/versionHistory/versionHistory";
import { VERSION_HISTORY_COPY } from "../constants/versionHistory.constants";
import { VersionHistoryGroup } from "../types/versionHistory.types";

export function useVersionRename(params: { onSaved: () => void; refetchVersions: () => void }) {
  const [editingVersionId, setEditingVersionId] = useState<string | null>(null);
  const { mutateAsync, isLoading } = useUpdateVersionMetadata();

  const startRename = (version: VersionHistoryGroup) => setEditingVersionId(version.versionId);
  const cancel = () => setEditingVersionId(null);

  /**
   * Returns a field-level error message on failure (e.g. the AC6 duplicate-name conflict), or
   * null on success -- the cell's own form applies it with `form.setError`, matching how
   * EditMappingDialog handles the same 409 via resolveDuplicateVersionNameError.
   */
  const commit = async (versionName: string): Promise<string | null> => {
    if (!editingVersionId) return null;

    try {
      await mutateAsync({ versionId: editingVersionId, versionName });
      cancel();
      successMessage(VERSION_HISTORY_COPY.renameSuccess(versionName));
      params.refetchVersions();
      params.onSaved();
      return null;
    } catch (err) {
      return resolveDuplicateVersionNameError(err) ?? "Failed to rename version. Please try again.";
    }
  };

  return { editingVersionId, isSaving: isLoading, startRename, cancel, commit };
}
